/**
 * NakshatraAngaInfoTable Component
 * Displays Nakshatra Anga division information with planets that fall in each nakshatra
 * Based on LakshmiDwadashamshaInfoTable.tsx but with nakshatra calculations and dual rulers
 */

import React, { useMemo } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Text,
    HStack
} from '@chakra-ui/react';
import {JatakaResponse} from '../../../../../models/api';
import {
    NAKSHATRA_ANGA_ATTRIBUTES,
    getNakshatraIndexFromLongitude,
    parseLongitudeFromPosition,
    getTaraPhalaForNakshatra,
    getTaraPhalaAttribute
} from '../data/NakshatraAngaTableData';
import {BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor} from '../../../../../config/ashtamshaColors';
import {createPlanetDisplay, PlanetDisplay} from '../helpers/planetDisplayHelper';
import {
    TABLE_WRAPPER_INLINE_STYLES,
    TABLE_CONTAINER_STYLES,
    TABLE_BASE_STYLES,
    TABLE_HEADER_ALT_STYLES,
    TABLE_ROW_HOVER_STYLES,
    TABLE_TH_BASE_STYLES,
    TABLE_TD_BASE_STYLES,
    TABLE_TD_SMALL_STYLES,
    TABLE_TD_CENTER_STYLES,
    PLANET_TEXT_STYLES,
    EMPTY_MESSAGE_BOX_STYLES,
    EMPTY_CELL_TEXT_STYLES,
    COLUMN_STYLES,
    getTaraPhalaColor
} from './styles/tableStyles';

export interface NakshatraAngaInfoTableProps {
    apiResponse?: JatakaResponse | null;
    calculatedData?: {
        planetsByNakshatra: Record<number, PlanetDisplay[]>;
        totalPlanets: number;
        calculationTime: number;
        chandraNakshatraIndex?: number | null;
    };
}

/**
 * Group planets by nakshatra they fall into (using traditional 360°/27 calculation)
 * Now includes Lagna and retrograde indication
 * Returns both planet grouping and Chandra's nakshatra index
 */
function groupPlanetsByNakshatra(apiResponse: JatakaResponse): { planetsByNakshatra: Record<number, PlanetDisplay[]>, chandraNakshatraIndex: number | null } {
    const planetsByNakshatra: Record<number, PlanetDisplay[]> = {};
    let chandraNakshatraIndex: number | null = null;

    // Initialize all nakshatras with empty arrays (1-27)
    for (let i = 1; i <= 27; i++) {
        planetsByNakshatra[i] = [];
    }

    // Get current hora planet for highlighting (same as Graha Chakra)
    const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

    // Process each planet INCLUDING LAGNA
    if (apiResponse.graha_positions?.planets) {
        apiResponse.graha_positions.planets.forEach(planet => {
            // Parse longitude from position (contains global degrees)
            const longitude = parseLongitudeFromPosition(planet.position);

            // Skip if longitude parsing failed
            if (!longitude) return;

            // Determine which nakshatra this planet falls into (1-27)
            const nakshatraIndex = getNakshatraIndexFromLongitude(longitude);

            // Track Chandra's nakshatra for Tara Phala
            if (planet.short_name === 'Ch') {
                chandraNakshatraIndex = nakshatraIndex;
            }

            // Use centralized planet display helper (DRY)
            const planetDisplay = createPlanetDisplay(planet, currentHoraPlanet);

            // Add to the appropriate nakshatra group
            planetsByNakshatra[nakshatraIndex].push(planetDisplay);

            // Debug logging only in development and limited to avoid memory issues
            // Removed excessive nakshatra calculation logs for performance
            // if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
            // }
        });
    }

    return { planetsByNakshatra, chandraNakshatraIndex };
}

/**
 * Main NakshatraAngaInfoTable component
 */
export const NakshatraAngaInfoTable: React.FC<NakshatraAngaInfoTableProps> = ({apiResponse, calculatedData}) => {
    // Use calculated data from lazy loading if available, otherwise fallback to synchronous calculation - MEMOIZED for performance
    const { planetsByNakshatra, chandraNakshatraIndex } = useMemo(() => {
        if (!apiResponse?.graha_positions?.planets) {
            return { planetsByNakshatra: {}, chandraNakshatraIndex: null };
        }

        // Use calculated data if available (from lazy calculator)
        if (calculatedData?.planetsByNakshatra) {
            return {
                planetsByNakshatra: calculatedData.planetsByNakshatra,
                chandraNakshatraIndex: calculatedData.chandraNakshatraIndex || null
            };
        }

        // Fallback to synchronous calculation
        return groupPlanetsByNakshatra(apiResponse);
    }, [apiResponse, calculatedData]);

    // Safety check - now after hooks
    if (!apiResponse?.graha_positions?.planets) {
        return (
            <Box {...EMPTY_MESSAGE_BOX_STYLES}>
                <Text>No planetary data available for Nakshatra Anga calculation</Text>
            </Box>
        );
    }

    return (
        <Box {...TABLE_WRAPPER_INLINE_STYLES}>
            <TableContainer {...TABLE_CONTAINER_STYLES}>
                <Table {...TABLE_BASE_STYLES}>
                    <Thead {...TABLE_HEADER_ALT_STYLES}>
                        <Tr>
                            <Th {...COLUMN_STYLES.large}>Planets</Th>
                            <Th {...COLUMN_STYLES.medium}>Nakshatra</Th>
                            <Th {...COLUMN_STYLES.small}>Rulers</Th>
                            <Th {...COLUMN_STYLES.huge}>Deity</Th>
                            <Th {...COLUMN_STYLES.xxlarge}>Quality</Th>
                            <Th {...COLUMN_STYLES.xxlarge}>Path</Th>
                            <Th {...COLUMN_STYLES.xlarge}>Direction</Th>
                            <Th {...COLUMN_STYLES.xxlarge}>Tara Phala</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {NAKSHATRA_ANGA_ATTRIBUTES.map((nakshatra) => {
                            const planetsInNakshatra = planetsByNakshatra[nakshatra.number] || [];

                            return (
                                <Tr key={nakshatra.number} _hover={TABLE_ROW_HOVER_STYLES}>
                                    <Td {...TABLE_TD_BASE_STYLES} minW="80px">
                                        {planetsInNakshatra.length > 0 ? (
                                            <HStack spacing={1} flexWrap="wrap">
                                                {planetsInNakshatra.map((planet, index) => {
                                                    // Use planetType from helper (DRY)
                                                    const colors = getAshtamshaPlanetColor(planet.planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

                                                    return (
                                                        <Text
                                                            key={`${planet.shortName}-${index}`}
                                                            {...PLANET_TEXT_STYLES}
                                                            color={colors.color}
                                                            bg={colors.backgroundColor}
                                                            border={colors.borderColor ? `1px solid` : 'none'}
                                                            borderColor={colors.borderColor}
                                                        >
                                                            {planet.displayText}
                                                        </Text>
                                                    );
                                                })}
                                            </HStack>
                                        ) : (
                                            <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                                        )}
                                    </Td>
                                    <Td {...TABLE_TD_BASE_STYLES} fontWeight="normal">
                                        {nakshatra.name_english}
                                    </Td>
                                    <Td {...TABLE_TD_CENTER_STYLES} fontWeight="normal">
                                        {nakshatra.rulers}
                                    </Td>
                                    <Td {...TABLE_TD_SMALL_STYLES}>
                                        {nakshatra.deity}
                                    </Td>
                                    <Td {...TABLE_TD_SMALL_STYLES}>
                                        {nakshatra.quality}
                                    </Td>
                                    <Td {...TABLE_TD_SMALL_STYLES}>
                                        {nakshatra.path}
                                    </Td>
                                    <Td {...TABLE_TD_SMALL_STYLES}>
                                        {nakshatra.direction}
                                    </Td>
                                    <Td {...TABLE_TD_SMALL_STYLES}>
                                        {chandraNakshatraIndex ? (() => {
                                            const taraNumber = getTaraPhalaForNakshatra(nakshatra.number, chandraNakshatraIndex);
                                            const taraAttr = getTaraPhalaAttribute(taraNumber);
                                            if (!taraAttr) return 'N/A';

                                            return (
                                                <Text
                                                    fontSize="sm"
                                                    color={getTaraPhalaColor(taraNumber)}
                                                    fontWeight="medium"
                                                >
                                                    {`${taraAttr.name_english} (${taraAttr.meaning})`}
                                                </Text>
                                            );
                                        })() : (
                                            <Text {...EMPTY_CELL_TEXT_STYLES}>N/A</Text>
                                        )}
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </TableContainer>
        </Box>
    );
};